import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTableColumnHeader } from "@/components/data-tables/data-table-column-header";
import { Eye, Trash2 } from "lucide-react";
import i18n from "@/utils/i18n/i18n";

interface Point {
    id: string | number;
    payload: Record<string, any>;
}

interface EmbeddingActions {
    onViewDetails?: (point: Point) => void;
    onDelete?: (pointId: string) => void;
}

export function createEmbeddingsColumns(actions: EmbeddingActions): ColumnDef<Point>[] {
    return [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                    className="translate-y-0.5"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    className="translate-y-0.5"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "id",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title={i18n.t("embeddings.id", "ID")}
                />
            ),
            cell: ({ row }) => {
                const id = row.original.id;
                const displayId =
                    typeof id === "string" ? id : id.toString();
                return (
                    <div className="max-w-[300px]">
                        <span className="font-mono text-xs break-all">{displayId}</span>
                    </div>
                );
            },
            enableSorting: true,
            enableHiding: true,
            size: 300,
        },
        {
            accessorKey: "payload",
            header: ({ column }) => (
                <DataTableColumnHeader
                    column={column}
                    title={i18n.t("embeddings.metadata", "Metadata")}
                />
            ),
            cell: ({ row }) => {
                const payload = row.original.payload;
                const entries = Object.entries(payload).slice(0, 3);
                const remaining = Object.keys(payload).length - 3;

                return (
                    <div className="flex flex-wrap gap-1">
                        {entries.map(([key, value]) => {
                            const stringValue =
                                typeof value === "string"
                                    ? value
                                    : JSON.stringify(value);
                            const truncated =
                                stringValue.length > 20
                                    ? `${stringValue.substring(0, 20)}...`
                                    : stringValue;
                            return (
                                <Badge
                                    key={key}
                                    variant="outline"
                                    className="text-xs"
                                >
                                    {key}: {truncated}
                                </Badge>
                            );
                        })}
                        {remaining > 0 && (
                            <Badge variant="outline" className="text-xs">
                                +{remaining} more
                            </Badge>
                        )}
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: true,
        },
        {
            id: "actions",
            header: () => (
                <div className="text-right">
                    {i18n.t("embeddings.actions", "Actions")}
                </div>
            ),
            cell: ({ row }) => {
                const point = row.original;

                return (
                    <div className="flex gap-2 justify-end">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => actions.onViewDetails?.(point)}
                        >
                            <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                actions.onDelete?.(point.id.toString())
                            }
                        >
                            <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                    </div>
                );
            },
            enableSorting: false,
            enableHiding: false,
        },
    ];
}

export const EmbeddingsSearchableColumns = [
    {
        id: "id" as keyof Point,
        get title() {
            return i18n.t("embeddings.id", "ID");
        },
    },
];
